import React, {forwardRef} from 'react'
import styled from 'styled-components'
import {Box, BoxProps, themeGet, useRefObjectAsForwardedRef} from '../..'
import {ArrowPlacement, RuleStepLabelProps} from './types'

const StyledArrow = styled(Box)<RuleStepLabelArrowProps>`
  position: absolute;
  background-color: ${p =>
    p.isSelected
      ? themeGet('colors.background.selected.bold.default')
      : themeGet('colors.background.neutral.bold.default')};

  clip-path: polygon(50% 0, 100% 100%, 0 100%);
  width: 22px;
  height: 6px;

  &[data-placement='left'] {
    transform: rotate(270deg);
    left: -13.9px;
    top: 8px;
  }
  &[data-placement='right'] {
    transform: rotate(90deg);
    right: -13.9px;
    top: 8px;
  }
  /* &:hover {
    background-color: ${themeGet('colors.background.accent.gray.bolder')};
    color: #ffffff;
    cursor: pointer;
  } */
`
const StyledStepLabel = styled(Box)<RuleStepLabelProps>`
  border-radius: ${p => (p.condition === 'when' ? themeGet('radii.1') : '')};
  padding: ${themeGet('space.1')} ${themeGet('space.2')};

  background-color: ${p =>
    p.isSelected
      ? themeGet('colors.background.selected.bold.default')
      : themeGet('colors.background.neutral.bold.default')};
  color: ${themeGet('colors.text.inverse')};

  font-weight: ${themeGet('fontWeights.semibold')};
  font-size: ${themeGet('fontSizes.2')};
  text-transform: uppercase;
  width: fit-content;
  user-select: none;
  // we need to interpolate the styles of both the arrow tips so that
  // when a user hovers over the label container, both the label container
  // and the arrow tip get the hover styles applied
  &:hover {
    /* background-color: ${themeGet('colors.background.accent.gray.bolder')}; */
    background-color: ${p =>
      p.isSelected
        ? themeGet('colors.background.selected.bold.hovered')
        : themeGet('colors.background.neutral.bold.hovered')};
  }
  color: ${themeGet('colors.text.inverse')};
  cursor: pointer;
`
// @TODO: there's probably a better way to do this than with polygons...
type RuleStepLabelArrowProps = {
  placement?: ArrowPlacement
  isSelected?: boolean
  isCurrentStep?: boolean
} & BoxProps

const RuleStepLabelArrow = ({placement, isSelected, ...props}: RuleStepLabelArrowProps) => {
  return <StyledArrow data-placement={placement} isSelected={isSelected} {...props} />
}

/**
 * Displays a Rule Builder Step Label, which describes a condition.
 */
const RuleStepLabel = forwardRef(
  (
    {
      label,
      description,
      condition,
      children,
      isSelected: isSelectedProp,
      isCompletedStep,
      testId,
      ...props
    }: RuleStepLabelProps & BoxProps,
    forwardedRef
  ) => {
    const containerRef = React.useRef<HTMLDivElement>(null)
    useRefObjectAsForwardedRef(forwardedRef, containerRef)

    const labelTestId = `${testId}-step-label`
    const wrapperTestId = `${testId}-step-label-wrapper`

    const isActive = isSelectedProp || isCompletedStep
    const shouldRender = !!label || !!description

    return shouldRender ? (
      <Box
        ref={containerRef}
        // onClick={handleRuleItemSelect}
        sx={{display: 'flex', position: 'relative', width: 'fit-content'}}
        data-testid={labelTestId}
      >
        {condition === 'if' ? <RuleStepLabelArrow placement="left" isSelected={isActive} /> : null}

        <StyledStepLabel isSelected={isActive} label={label} condition={condition} data-testid={labelTestId}>
          {label}
        </StyledStepLabel>
        {condition === 'if' || condition === 'then' ? (
          <RuleStepLabelArrow isSelected={isActive} placement="right" />
        ) : null}
      </Box>
    ) : null
  }
)

RuleStepLabel.displayName = 'RuleStepLabel'
export default RuleStepLabel
